import { useState, useEffect } from 'react';
import { Lock, Mail, Shield, BookOpen, ClipboardCheck, CheckCircle2, AlertTriangle, Save, Award, Download, LogOut, IdCard, Radiation } from 'lucide-react';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import { printCertificate } from '../lib/certificate';
import DatePickerField from '../components/DatePickerField';
import EvidenceUploads from '../components/EvidenceUploads';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [certs, setCerts] = useState([]);
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwMsg, setPwMsg] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);
  const [revokeBusy, setRevokeBusy] = useState(false);
  const [revokeMsg, setRevokeMsg] = useState(null);

  useEffect(() => { loadProfile(); loadCerts(); }, []);

  const loadProfile = async () => {
    try {
      const r = await axios.get(`${API}/profile`, { withCredentials: true });
      setProfile(r.data);
    } catch { /* ignore */ }
  };

  const loadCerts = async () => {
    try {
      const r = await axios.get(`${API}/training/certificates`, { withCredentials: true });
      setCerts(r.data);
    } catch { /* ignore */ }
  };

  const downloadCert = async (moduleId) => {
    // Server-rendered PDF via WeasyPrint — opens in a new tab and the browser
    // shows it inline with native save/print controls. Falls back to the
    // legacy client-side render if the request fails.
    const url = `${API}/training/certificate/${moduleId}/pdf`;
    try {
      const r = await fetch(url, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      try {
        const r = await axios.get(`${API}/training/certificate/${moduleId}`, { withCredentials: true });
        printCertificate(r.data);
      } catch (e2) {
        alert(e2.response?.data?.detail || err.message || 'Failed to generate certificate');
      }
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw !== confirmPw) { setPwMsg({ type: 'error', text: 'Passwords do not match' }); return; }
    if (newPw.length < 8) { setPwMsg({ type: 'error', text: 'Password must be at least 8 characters' }); return; }
    setPwSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { old_password: oldPw, new_password: newPw }, { withCredentials: true });
      setPwMsg({ type: 'success', text: 'Password updated successfully' });
      setOldPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      setPwMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    }
    setPwSaving(false);
  };

  const revokeSessions = async () => {
    if (!window.confirm('Sign out of all other devices? Every other browser or phone currently signed in as you will be forced to log back in.')) return;
    setRevokeBusy(true); setRevokeMsg(null);
    try {
      const r = await axios.post(`${API}/auth/revoke-sessions`, {}, { withCredentials: true });
      setRevokeMsg({ type: 'success', text: `All other sessions signed out (v${r.data.token_version}).` });
    } catch (err) {
      setRevokeMsg({ type: 'error', text: err.response?.data?.detail || 'Failed to revoke sessions' });
    }
    setRevokeBusy(false);
  };

  if (!user) return null;

  const roleColors = { admin: '#42CDD7', superuser: '#7C3AED', radiologist: '#F59E0B', reception: '#64748B', radiographer: '#0EA5E9', sonographer: '#14B8A6', nursing: '#EC4899', staff: '#64748B' };
  const roleLabel = { admin: 'Admin', superuser: 'Practice Manager', radiologist: 'Radiologist', reception: 'Reception', radiographer: 'Radiographer', sonographer: 'Sonographer', nursing: 'Nursing', staff: 'Staff' };

  return (
    <div className="min-h-screen bg-background" data-testid="profile-page">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-8">
          <h1 className="font-['Outfit'] text-3xl font-bold text-foreground" data-testid="profile-title">My Profile</h1>
          <p className="text-sm text-muted-foreground mt-1">Your account, training progress, certificates, and security</p>
        </div>

        {/* Identity card */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="profile-identity">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold text-white" style={{ backgroundColor: roleColors[user.role] || '#64748B' }}>
              {user.name?.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-['Outfit'] text-xl font-semibold text-foreground">{user.name}</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-0.5"><Mail className="w-3.5 h-3.5" />{user.email}</p>
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${roleColors[user.role]}1A`, color: roleColors[user.role] }}>
                  <Shield className="w-3 h-3" /> {roleLabel[user.role]}
                </span>
                {profile?.user?.totp_enabled && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/10 text-green-600">
                    <CheckCircle2 className="w-3 h-3" /> 2FA Active
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Compliance snapshot */}
        {profile && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="profile-snapshot">
            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen className="w-4 h-4" style={{ color: '#2563EB' }} />
                <h3 className="text-sm font-semibold text-foreground">Training progress</h3>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-['Outfit'] text-4xl font-bold text-foreground">{profile.training.progress_pct}%</span>
                <span className="text-xs text-muted-foreground">completed</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                <div className="h-full rounded-full transition-all" style={{ width: `${profile.training.progress_pct}%`, backgroundColor: '#2563EB' }} />
              </div>
              <p className="text-xs text-muted-foreground">
                {profile.training.completed_steps} of {profile.training.total_steps} steps · {profile.training.total_modules} modules
              </p>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-4 h-4" style={{ color: '#7C3AED' }} />
                <h3 className="text-sm font-semibold text-foreground">SOP compliance</h3>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-['Outfit'] text-4xl font-bold text-foreground">{profile.sops.rate}%</span>
                <span className="text-xs text-muted-foreground">rate</span>
              </div>
              <div className="flex items-center gap-3 text-xs mt-2">
                <span className="text-green-600 font-medium">{profile.sops.completed} done</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-amber-600 font-medium">{profile.sops.total - profile.sops.completed} pending</span>
                {profile.sops.overdue > 0 && <><span className="text-muted-foreground">·</span><span className="text-red-600 font-medium inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{profile.sops.overdue} overdue</span></>}
              </div>
            </div>
          </div>
        )}

        {/* Licensing & accreditations (DIAS Phase B) */}
        <LicensingCard profile={profile} reload={loadProfile} />

        {/* Training certificates */}
        <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="profile-certificates">
          <div className="flex items-center gap-2 mb-3">
            <Award className="w-4 h-4" style={{ color: '#F59E0B' }} />
            <h3 className="text-sm font-semibold text-foreground">Training certificates</h3>
            {certs.length > 0 && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600">{certs.length} earned</span>}
          </div>
          {certs.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">
              Complete 100% of any K4 training module to earn a certificate. Certificates are printable / save-as-PDF ready for CPD and audit records.
            </p>
          ) : (
            <div className="space-y-2">
              {certs.map((c, i) => (
                <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-muted/40 border border-border" data-testid={`cert-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: 'rgba(245,158,11,0.12)' }}>
                      <Award className="w-4 h-4" style={{ color: '#F59E0B' }} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-foreground truncate">{c.module_title}</p>
                      <p className="text-xs text-muted-foreground">{c.total_steps} steps · completed {c.completed_on}</p>
                    </div>
                  </div>
                  <button onClick={() => downloadCert(c.module_id)} data-testid={`cert-download-${i}`}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-border hover:bg-accent transition-colors">
                    <Download className="w-3.5 h-3.5" /> Download PDF
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Change password */}
        <div className="bg-card border border-border rounded-2xl p-6" data-testid="profile-change-password">
          <div className="flex items-center gap-2 mb-4">
            <Lock className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Change password</h3>
          </div>
          <form onSubmit={changePassword} className="space-y-3 max-w-md">
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Current password</label>
              <input type="password" value={oldPw} onChange={e => setOldPw(e.target.value)} required data-testid="pw-current"
                className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">New password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} required minLength={8} data-testid="pw-new"
                className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30" />
            </div>
            <div>
              <label className="block text-xs font-medium text-muted-foreground mb-1">Confirm new password</label>
              <input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} required data-testid="pw-confirm"
                className="w-full px-3 py-2.5 text-sm bg-muted border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#42CDD7]/30" />
            </div>
            {pwMsg && (
              <div className={`text-sm px-3 py-2 rounded-lg ${pwMsg.type === 'error' ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600'}`} data-testid="pw-msg">
                {pwMsg.text}
              </div>
            )}
            <button type="submit" disabled={pwSaving} data-testid="pw-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-40" style={{ backgroundColor: '#42CDD7' }}>
              <Save className="w-4 h-4" /> {pwSaving ? 'Saving...' : 'Update password'}
            </button>
          </form>
        </div>

        {/* Active sessions */}
        <div className="bg-card border border-border rounded-2xl p-6 mt-6" data-testid="profile-sessions">
          <div className="flex items-center gap-2 mb-2">
            <LogOut className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold text-foreground">Active sessions</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-4 max-w-lg">
            Signed in on a second device you no longer have? Force every other browser and phone to sign out immediately. This device stays signed in.
          </p>
          {revokeMsg && (
            <div className={`text-sm px-3 py-2 rounded-lg mb-3 max-w-md ${revokeMsg.type === 'error' ? 'bg-red-500/10 text-red-600' : 'bg-green-500/10 text-green-600'}`} data-testid="revoke-msg">
              {revokeMsg.text}
            </div>
          )}
          <button onClick={revokeSessions} disabled={revokeBusy} data-testid="revoke-sessions-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-lg border-2 text-red-600 border-red-500/40 hover:bg-red-500/5 disabled:opacity-40">
            <LogOut className="w-4 h-4" /> {revokeBusy ? 'Revoking…' : 'Sign out of all other devices'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// LicensingCard — DIAS Phase B staff licensing self-service
// ─────────────────────────────────────────────────────────────────────────────

const ACCREDITATION_OPTIONS = [
  'CT-licensed operator', 'MRI safety trained', 'Contrast injection',
  'BLS / CPR', 'ALS / ACLS', 'Cannulation', 'Pregnancy declaration',
];

function statusFor(dateStr, withinDays = 30) {
  if (!dateStr) return { state: 'missing', daysLeft: null };
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return { state: 'missing', daysLeft: null };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { state: 'expired', daysLeft: diff };
  if (diff <= withinDays) return { state: 'expiring', daysLeft: diff };
  return { state: 'current', daysLeft: diff };
}

function StatusChip({ dateStr }) {
  const { state, daysLeft } = statusFor(dateStr);
  if (state === 'missing') return <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-muted text-muted-foreground">Not set</span>;
  if (state === 'expired') return <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">Expired {Math.abs(daysLeft)}d ago</span>;
  if (state === 'expiring') return <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Expires in {daysLeft}d</span>;
  return <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-500/10 text-green-600">Current</span>;
}

function LicensingCard({ profile, reload }) {
  const initial = profile?.user?.licensing || {};
  const [form, setForm] = useState({
    ahpra_number: '', ahpra_expiry: '',
    radiation_licence_no: '', radiation_licence_expiry: '',
    asar_number: '', asar_expiry: '',
    bls_expiry: '', contrast_accreditation_expiry: '',
    accreditations: [], cpd_hours_ytd: 0, cpd_year: new Date().getFullYear(),
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (profile?.user?.licensing) {
      setForm(f => ({ ...f, ...profile.user.licensing,
        accreditations: profile.user.licensing.accreditations || [],
        cpd_hours_ytd: profile.user.licensing.cpd_hours_ytd || 0,
        cpd_year: profile.user.licensing.cpd_year || new Date().getFullYear(),
      }));
    }
    // eslint-disable-next-line
  }, [profile?.user?.email]);

  const set = (k, v) => setForm(s => ({ ...s, [k]: v }));
  const toggleAcc = (a) => setForm(s => ({
    ...s,
    accreditations: s.accreditations.includes(a)
      ? s.accreditations.filter(x => x !== a)
      : [...s.accreditations, a],
  }));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/users/me/licensing`, form, { withCredentials: true });
      setMsg({ type: 'success', text: 'Licensing details saved.' });
      if (reload) await reload();
    } catch (e) {
      setMsg({ type: 'error', text: e.response?.data?.detail || 'Save failed.' });
    }
    setSaving(false);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-6 mb-6" data-testid="profile-licensing">
      <div className="flex items-center gap-2 mb-3">
        <IdCard className="w-4 h-4" style={{ color: '#0EA5E9' }} />
        <h3 className="text-sm font-semibold text-foreground">Licensing & accreditations</h3>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-600">DIAS</span>
      </div>
      <p className="text-xs text-muted-foreground mb-4">Required for QIP audit. You'll receive an email reminder 30 days and 7 days before any expiry.</p>

      {msg && <div className={`rounded-lg border p-2.5 text-xs mb-3 flex items-start gap-2 ${msg.type === 'success' ? 'bg-green-500/5 border-green-500/30 text-green-600' : 'bg-red-500/5 border-red-500/30 text-red-600'}`}>
        {msg.type === 'success' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />} {msg.text}
      </div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <LicField label="AHPRA registration #" value={form.ahpra_number} onChange={v => set('ahpra_number', v)} testid="lic-ahpra-no" placeholder="e.g. MED0001234567" />
        <LicDate label="AHPRA expiry" value={form.ahpra_expiry} onChange={v => set('ahpra_expiry', v)} testid="lic-ahpra-exp" />
        <LicField label="Radiation use licence #" value={form.radiation_licence_no} onChange={v => set('radiation_licence_no', v)} testid="lic-rad-no" placeholder="e.g. NSW EPA / state licence" />
        <LicDate label="Radiation licence expiry" value={form.radiation_licence_expiry} onChange={v => set('radiation_licence_expiry', v)} testid="lic-rad-exp" icon={<Radiation className="w-3 h-3" style={{ color: '#7C3AED' }} />} />
        <LicField label="ASAR registration # (sonographer)" value={form.asar_number} onChange={v => set('asar_number', v)} testid="lic-asar-no" placeholder="e.g. ASAR-12345" />
        <LicDate label="ASAR expiry" value={form.asar_expiry} onChange={v => set('asar_expiry', v)} testid="lic-asar-exp" />
        <LicDate label="BLS / CPR expiry" value={form.bls_expiry} onChange={v => set('bls_expiry', v)} testid="lic-bls-exp" />
        <LicDate label="Contrast accreditation expiry" value={form.contrast_accreditation_expiry} onChange={v => set('contrast_accreditation_expiry', v)} testid="lic-contrast-exp" />
      </div>

      <div className="mt-4">
        <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Accreditations</span>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {ACCREDITATION_OPTIONS.map(a => {
            const on = form.accreditations.includes(a);
            return (
              <button key={a} onClick={() => toggleAcc(a)} type="button" data-testid={`lic-acc-${a.replace(/\s+/g, '-').toLowerCase()}`}
                className="text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors"
                style={on
                  ? { backgroundColor: '#0EA5E915', borderColor: '#0EA5E940', color: '#0EA5E9' }
                  : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
                {a}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3 items-end">
        <LicField label="CPD hours YTD" value={form.cpd_hours_ytd} onChange={v => set('cpd_hours_ytd', Number(v) || 0)} testid="lic-cpd-hours" />
        <LicField label="CPD year" value={form.cpd_year} onChange={v => set('cpd_year', Number(v) || new Date().getFullYear())} testid="lic-cpd-year" />
        <button onClick={save} disabled={saving} data-testid="lic-save-btn"
          className="inline-flex items-center justify-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50"
          style={{ backgroundColor: '#0EA5E9' }}>
          <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save licensing'}
        </button>
      </div>

      {/* Status overview */}
      <div className="mt-4 pt-3 border-t border-border grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <div className="flex items-center justify-between"><span>AHPRA</span><StatusChip dateStr={initial.ahpra_expiry} /></div>
        <div className="flex items-center justify-between"><span>Radiation</span><StatusChip dateStr={initial.radiation_licence_expiry} /></div>
        <div className="flex items-center justify-between"><span>BLS</span><StatusChip dateStr={initial.bls_expiry} /></div>
        <div className="flex items-center justify-between"><span>Contrast</span><StatusChip dateStr={initial.contrast_accreditation_expiry} /></div>
      </div>

      {/* Licence document uploads */}
      <div className="mt-4 pt-3 border-t border-border">
        <EvidenceUploads parentType="user_licence" parentId={profile?.user?.id || profile?.user?._id || ''}
          label="Staff licence PDFs (AHPRA / radiation / BLS / contrast)"
          testidPrefix="lic-evidence" />
      </div>
    </div>
  );
}

function LicField({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <input type="text" value={value ?? ''} placeholder={placeholder}
        onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2" />
    </label>
  );
}

function LicDate({ label, value, onChange, testid, icon }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
        {icon} {label}
      </span>
      <DatePickerField value={value} onChange={onChange} format="iso" testid={testid} />
    </label>
  );
}
